// Builds one kissaderby.fi cat from the shared base cat: clone, coat, fur, eyes and build.
import {
  type AnimationClip, AnimationMixer, type Bone, Group, Matrix4, type Material, type MeshStandardMaterial, type Object3D,
  PropertyBinding, Quaternion, SkinnedMesh, type Texture, Vector3, type WebGLRenderer,
} from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import type { BuildSpec, CatDef } from '../../data/cats';
import { CAT_DISPLAY_SCALE } from '../../data/cats';
import { bakeCoat, type CoatMaps } from './coatMaterial';
import { FurMaterials } from './fur';
import { dressEyeball, enlargeIris, EyeMaterials } from './eyes';
import { createBacon } from './props';
/** The shipped base cat, built from assets-src/base-cat by `npm run assets` (WebP textures, resampled clips). */
export const BASE_CAT_URL = `${import.meta.env.BASE_URL}assets/cat/base-cat.glb`;

export interface ClipInfo { duration: number; loop: boolean; stride?: number; speed?: number }

/** The loaded base cat, prepared once and shared by all six cats. */
export interface BaseCat {
  scene: Object3D;
  clips: Map<string, AnimationClip>;
  info: Record<string, ClipInfo>;
  /** The base model's own fur map, used for fine detail in the coat bake. */
  detail: Texture | null;
}

export async function loadBaseCat(url = BASE_CAT_URL, loader = new GLTFLoader()): Promise<BaseCat> {
  return prepareBaseCat(await loader.loadAsync(url));
}

export function prepareBaseCat(gltf: GLTF): BaseCat {
  const clips = new Map<string, AnimationClip>();
  for (const clip of gltf.animations) {
    // The clips key every bone's scale at 1. Dropping those tracks lets each cat's build scale its
    // head, ears and chest without fighting the animation (and saves a third of the mixer work).
    clip.tracks = clip.tracks.filter((t) => !t.name.endsWith('.scale'));
    clips.set(clip.name, clip);
  }
  let info: Record<string, ClipInfo> = {};
  gltf.scene.traverse((o) => {
    const raw = (o.userData as { catDerby?: { clips?: string } }).catDerby?.clips;
    if (raw) info = JSON.parse(raw) as Record<string, ClipInfo>;
  });
  let detail: Texture | null = null;
  gltf.scene.traverse((o) => {
    const m = o as SkinnedMesh;
    if (m.isSkinnedMesh && m.name === 'lod0_body') detail = (m.material as MeshStandardMaterial).map ?? null;
  });
  return { scene: gltf.scene, clips, info, detail };
}

// ------------------------------------------------------------------ build

/** Bone names as three stores them (GLTFLoader drops the dots: "ear.L.001" becomes "earL001"). */
export function boneName(blenderName: string): string {
  return PropertyBinding.sanitizeNodeName(blenderName);
}

const LEG_FRONT = ['upper_arm.001', 'forearm', 'forearm.001', 'hand', 'hand.001'];
const LEG_HIND = ['thigh.001', 'shin', 'shin.001', 'foot', 'foot.001'];
const BODY = ['spine.001', 'spine.002', 'spine.003'];
const NECK = ['spine.004', 'spine.005', 'spine.006'];
const TAIL = ['tail.002', 'tail.003', 'tail.004', 'tail.005', 'tail.006'];

interface BoneEdit { bone: Bone; length: number; scale: number }

/** Bone changes for a build. Applied after every animation update (the clips key bone positions). */
export function buildEdits(bones: Map<string, Bone>, b: BuildSpec): BoneEdit[] {
  const edits = new Map<Bone, BoneEdit>();
  const edit = (name: string, length = 1, scale = 1) => {
    const bone = bones.get(boneName(name));
    if (!bone) return;
    const e = edits.get(bone) ?? { bone, length: 1, scale: 1 };
    e.length *= length; e.scale *= scale;
    edits.set(bone, e);
  };
  for (const side of ['L', 'R']) {
    for (const n of [...LEG_FRONT, ...LEG_HIND]) {
      const [base, num] = n.split('.') as [string, string | undefined];
      edit(num ? `${base}.${side}.${num}` : `${base}.${side}`, b.legLength);
    }
    edit(`ear.${side}`, 1, b.ears);
    edit(`eye.${side}`, 1, b.eyes);
  }
  for (const n of BODY) edit(n, b.bodyLength);
  for (const n of NECK) edit(n, b.neckLength);
  for (const n of TAIL) edit(n, b.tailLength);
  edit('spine.006', 1, b.head);
  edit('Breast.C', 1, b.girth);
  edit('belly.C', 1, b.girth);
  return [...edits.values()].filter((e) => e.length !== 1 || e.scale !== 1);
}

export function applyEdits(edits: BoneEdit[]): void {
  for (const e of edits) {
    if (e.length !== 1) e.bone.position.multiplyScalar(e.length);
    if (e.scale !== 1) e.bone.scale.setScalar(e.scale);
  }
}

/** Undoes applyEdits' lengths (scales stay: no clip keys them). */
export function removeEdits(edits: BoneEdit[]): void {
  for (const e of edits) if (e.length !== 1) e.bone.position.divideScalar(e.length);
}

// ------------------------------------------------------------------ the model

export type CatLod = 0 | 1;

const _m = new Matrix4();
const _v = new Vector3();

/** One cat's renderable: the cloned rig with its coat, fur, eyes and the bacon it can hold. */
export class CatModel {
  /** Put this in the scene. Its children are the rig; move and turn the model, not the rig. */
  readonly root = new Group();
  readonly rig: Object3D;
  readonly bones = new Map<string, Bone>();
  readonly fur: FurMaterials;
  readonly eyes: EyeMaterials;
  /** The bacon strip held across the mouth while eating. */
  readonly mouthBacon: Object3D;
  /** Height to lift the rig so longer or shorter legs still stand on the ground. */
  readonly groundOffset: number;
  private edits: BoneEdit[];
  private lodMeshes: Record<CatLod, SkinnedMesh[]> = { 0: [], 1: [] };
  private shellMeshes: Record<CatLod, SkinnedMesh[]> = { 0: [], 1: [] };
  private lod: CatLod = 0;
  private shells = 0;
  private maps: CoatMaps;

  constructor(renderer: WebGLRenderer, base: BaseCat, readonly def: CatDef, maxShells: number) {
    this.rig = SkeletonUtils.clone(base.scene);
    this.rig.name = `cat-${def.id}`;
    this.root.name = `cat-${def.id}`;
    this.root.add(this.rig);
    this.rig.traverse((o) => { if ((o as Bone).isBone) this.bones.set(o.name, o as Bone); });

    let bodyGeometry = null as SkinnedMesh['geometry'] | null;
    this.rig.traverse((o) => { const m = o as SkinnedMesh; if (m.isSkinnedMesh && m.name === 'lod0_body') bodyGeometry = m.geometry; });
    if (!bodyGeometry) throw new Error('base cat: no lod0_body');
    this.maps = bakeCoat(renderer, bodyGeometry, def.coat, base.detail, def.lane * 3.7);
    this.fur = new FurMaterials({ colour: this.maps.colour, info: this.maps.info, coat: def.coat }, maxShells);
    this.eyes = new EyeMaterials(def.eyes);

    const skinned: SkinnedMesh[] = [];
    this.rig.traverse((o) => { if ((o as SkinnedMesh).isSkinnedMesh) skinned.push(o as SkinnedMesh); });
    for (const m of skinned) {
      const lod: CatLod = m.name.startsWith('lod1_') ? 1 : 0;
      m.frustumCulled = false; // skinned bounds do not follow the pose; the field culls whole cats
      m.castShadow = true;
      m.receiveShadow = true;
      this.lodMeshes[lod].push(m);
      if (/_body$/.test(m.name)) {
        m.material = this.fur.skin;
        for (let i = 0; i < maxShells; i++) {
          const s = new SkinnedMesh(m.geometry, this.fur.shells[i]!);
          s.bind(m.skeleton, m.bindMatrix);
          s.name = `${m.name}_shell${i + 1}`;
          // The solid body supplies the silhouette shadow; fine blended hairs do not
          // cast extra opaque copies of that surface.
          s.castShadow = false;
          s.receiveShadow = true;
          s.renderOrder = i + 1; // blend from roots outwards, independent of object sorting
          s.frustumCulled = false;
          m.parent!.add(s);
          this.shellMeshes[lod].push(s);
        }
      } else if (/_iris$/.test(m.name)) {
        m.geometry = enlargeIris(m.geometry, def.eyes.irisScale);
        m.material = this.eyes.iris;
        m.castShadow = false;
      } else if (/_eyes$/.test(m.name)) {
        const back = dressEyeball(m, this.eyes);
        back.frustumCulled = false;
        this.lodMeshes[lod].push(back);
      } else if (/_fur$/.test(m.name)) {
        // Original tapered geometry from the model build: no opaque source fur cards.
        const mat = (m.material as MeshStandardMaterial).clone();
        mat.color.set(def.id === 'tyyne' || def.id === 'misse' ? '#c5beb0' : '#d4cabb');
        mat.roughness = 1;
        mat.metalness = 0;
        mat.envMapIntensity = 0;
        mat.transparent = true;
        mat.depthWrite = false;
        mat.opacity = 0.4;
        // SMAA cannot recover the coverage of a tube narrower than one raster pixel.
        // Fade those fine furnishings instead of letting them flash as ivory dots.
        // The build's mid-strand diameter is about 0.18 mm; account for the posed
        // model's display scale, then compare it with view-space metres per pixel.
        mat.onBeforeCompile = (sh) => {
          sh.vertexShader = sh.vertexShader
            .replace('#include <common>', '#include <common>\nvarying float vStrandWidth;')
            // Give near strands enough raster coverage to form a continuous line.
            // Lower opacity compensates for this small radial expansion; distant
            // coverage still uses the original fine diameter and fades away.
            .replace('#include <begin_vertex>', `#include <begin_vertex>
              transformed += normalize(objectNormal) * 0.0002;`)
            .replace('#include <project_vertex>', `#include <project_vertex>
              vStrandWidth = 0.00018 * length(modelViewMatrix[0].xyz);`);
          sh.fragmentShader = sh.fragmentShader
            .replace('#include <common>', '#include <common>\nvarying float vStrandWidth;')
            .replace('#include <alphamap_fragment>', `#include <alphamap_fragment>
              float strandPixelSize = max(length(dFdx(vViewPosition)), length(dFdy(vViewPosition)));
              float strandCoverage = vStrandWidth / max(strandPixelSize, 0.000001);
              diffuseColor.a *= smoothstep(0.15, 0.85, strandCoverage);`);
        };
        mat.customProgramCacheKey = () => 'cat-furnishing-coverage-soft-width';
        m.material = mat as Material;
        m.castShadow = false;
      }
    }

    // build: proportions, size, and the lift that keeps the paws on the ground
    this.edits = buildEdits(this.bones, def.build);
    this.rig.scale.setScalar(def.build.size * CAT_DISPLAY_SCALE);
    this.groundOffset = this.measureGroundOffset(base);
    this.rig.position.y = this.groundOffset;

    this.mouthBacon = this.attachMouthBacon(skinned.find((m) => m.name === 'lod0_body')!);
    this.setLod(0);
    this.setShells(maxShells);
  }

  /**
   * The mixer only writes a bone when its clip value changes, so the build is applied after each
   * mixer update and removed again before the next one. Always call them as a pair.
   */
  applyBuild(): void {
    if (this.built) return;
    applyEdits(this.edits);
    this.built = true;
  }

  removeBuild(): void {
    if (!this.built) return;
    removeEdits(this.edits);
    this.built = false;
  }
  private built = false;

  setLod(lod: CatLod): void {
    this.lod = lod;
    for (const l of [0, 1] as const) for (const m of this.lodMeshes[l]) m.visible = l === lod;
    this.updateShellVisibility();
  }

  get currentLod(): CatLod { return this.lod; }

  /** Shells to draw (0 at the Low tier or far away). */
  setShells(n: number): void {
    this.shells = Math.max(0, Math.min(this.fur.maxShells, Math.round(n)));
    this.fur.setShellsVisible(this.shells);
    this.updateShellVisibility();
  }

  get shellCount(): number { return this.shells; }

  setTrip(amount: number, timeS: number): void { this.eyes.setTrip(amount, timeS); }

  dispose(): void {
    this.fur.dispose(); this.eyes.dispose(); this.maps.dispose();
  }

  private updateShellVisibility(): void {
    for (const l of [0, 1] as const) this.shellMeshes[l].forEach((m, i) => { m.visible = l === this.lod && i < this.shells; });
  }

  /** How far the build moved the paws relative to the base cat, measured in the idle pose. */
  private measureGroundOffset(base: BaseCat): number {
    const idle = base.clips.get('idle');
    const toes = ['f_toe.L', 'f_toe.R', 'r_toe.L', 'r_toe.R'].map((n) => this.bones.get(boneName(n))).filter((b): b is Bone => !!b);
    if (!idle || toes.length === 0) return 0;
    const lowest = (withBuild: boolean) => {
      poseAt(this.rig, idle, 0);
      if (withBuild) applyEdits(this.edits);
      this.rig.updateMatrixWorld(true);
      return Math.min(...toes.map((b) => b.getWorldPosition(_v).y));
    };
    const plain = lowest(false);
    const built = lowest(true);
    poseAt(this.rig, idle, 0); // leave the plain pose as the mixer's starting state
    return plain - built;
  }

  /** Bacon across the mouth, parented to the jaw so it moves with chewing. */
  private attachMouthBacon(body: SkinnedMesh): Object3D {
    const bacon = createBacon(0.95);
    bacon.castShadow = false;
    const jaw = this.bones.get(boneName('jaw'))!;
    const index = body.skeleton.bones.indexOf(jaw);
    // where it sits in the rest pose (mesh space), then expressed in the jaw's bind space
    const rest = new Matrix4().compose(new Vector3(0, 0.229, 0.303), new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), 0.06), new Vector3(1, 1, 1));
    _m.copy(body.skeleton.boneInverses[index]!).multiply(body.bindMatrix).multiply(rest);
    _m.decompose(bacon.position, bacon.quaternion, bacon.scale);
    jaw.add(bacon);
    bacon.visible = false;
    return bacon;
  }
}

/** Poses a rig at a clip time (for measuring), leaving no mixer behind. */
function poseAt(rig: Object3D, clip: AnimationClip, t: number): void {
  const mixer = new AnimationMixer(rig);
  mixer.clipAction(clip).play();
  mixer.setTime(t);
  mixer.stopAllAction();
  mixer.uncacheRoot(rig);
}
